import React, { useState, useEffect } from 'react';
import { 
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, TextField, FormControl, InputLabel, Select, MenuItem, IconButton 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { collection, getDoc, setDoc, doc, query, where, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';

const AddGst = ({ companyCode }) => {
  const [gstPlans, setGstPlans] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newGst, setNewGst] = useState({ gstName: '', percentage: '', inclusive: 'false' });

  const currentUser = auth.currentUser;
      const userId = currentUser ? currentUser.uid : "unknown";

  useEffect(() => {
    if (!companyCode) {
      console.error('AddGst: No companyCode provided');
      return;
    }

    const gstQuery = query(collection(db, 'gstPlans'), where('companyCode', '==', companyCode));

    const unsubscribe = onSnapshot(gstQuery, (querySnapshot) => {
      const gstList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setGstPlans(gstList);
      console.log(`AddGst: Real-time update - fetched ${gstList.length} GST plans for companyCode: ${companyCode}`);
    }, (error) => {
      console.error('AddGst: Error fetching GST plans in real-time:', error);
    });

    return () => {
      unsubscribe();
      console.log('AddGst: Cleaned up Firestore listener');
    };
  }, [companyCode]);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    setNewGst({ gstName: '', percentage: '', inclusive: 'false' });
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleNewGstChange = (e) => {
    const { name, value } = e.target;
    setNewGst(prev => ({ ...prev, [name]: value }));
  };

  const handleAddGst = async () => {
    if (!newGst.gstName || !newGst.percentage) {
      alert('Please enter a GST name and percentage');
      return;
    }
    const percentageValue = parseFloat(newGst.percentage);
    if (isNaN(percentageValue) || percentageValue < 0 || percentageValue > 100) {
      alert('Please enter a valid percentage between 0 and 100');
      return;
    }
    try {
      const sanitizedGstName = newGst.gstName.replace(/\s+/g, '_');
      const docId = `${companyCode}_${sanitizedGstName}`;
      const gstDocRef = doc(db, 'gstPlans', docId);
      const gstDoc = await getDoc(gstDocRef);
      if (gstDoc.exists()) {
        alert(`A GST plan with name ${newGst.gstName} already exists for this company.`);
        return;
      }
      await setDoc(gstDocRef, {
        gstName: newGst.gstName,
        percentage: percentageValue,
        inclusive: newGst.inclusive === 'true', // Ensure boolean value
        companyCode,
        createdAt: new Date().toISOString(), // Add timestamp for tracking
        createdBy: userId
      });
      console.log(`AddGst: Added GST plan with ID: ${docId}, Name: ${newGst.gstName}, Percentage: ${percentageValue}%, Inclusive: ${newGst.inclusive === 'true' ? 'Yes' : 'No'}`);
      handleCloseAddDialog();
    } catch (error) {
      console.error('AddGst: Error adding GST plan:', error);
      alert(`Failed to add GST plan: ${error.message}`);
    }
  };

  return (
    <Box sx={{ p: 4, bgcolor: 'background.default', position: 'relative' }}>
      <Typography variant="h4" gutterBottom>
        GST Plans
      </Typography>
      <IconButton
        color="primary"
        onClick={handleOpenAddDialog}
        sx={{ position: 'absolute', top: 16, right: 16 }}
        aria-label="Add GST"
      >
        <AddIcon fontSize="large" />
        <Typography variant="button" sx={{ ml: 1 }}>
          Add GST
        </Typography>
      </IconButton>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><Typography variant="subtitle1">GST Name</Typography></TableCell>
              <TableCell><Typography variant="subtitle1">Percentage</Typography></TableCell>
              <TableCell><Typography variant="subtitle1">Inclusive</Typography></TableCell>
              <TableCell><Typography variant="subtitle1">Created At</Typography></TableCell> {/* New column */}
            </TableRow>
          </TableHead>
          <TableBody>
            {gstPlans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body1" color="text.secondary">
                    No GST plans available for this company.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              gstPlans.map(gst => (
                <TableRow key={gst.id}>
                  <TableCell>{gst.gstName}</TableCell>
                  <TableCell>{gst.percentage}%</TableCell>
                  <TableCell>{gst.inclusive ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{gst.createdAt || 'N/A'}</TableCell> {/* Display creation time */}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog}>
        <DialogTitle>Add New GST Plan</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="gstName"
            label="GST Name"
            type="text"
            fullWidth
            value={newGst.gstName}
            onChange={handleNewGstChange}
          />
          <TextField
            margin="dense"
            name="percentage"
            label="Percentage"
            type="number"
            fullWidth
            value={newGst.percentage}
            onChange={handleNewGstChange}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            helperText="Enter percentage (0-100)"
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="inclusive-select-label">Inclusive</InputLabel>
            <Select
              labelId="inclusive-select-label"
              name="inclusive"
              value={newGst.inclusive}
              label="Inclusive"
              onChange={handleNewGstChange}
            >
              <MenuItem value="true">Yes (GST included in price)</MenuItem>
              <MenuItem value="false">No (GST added on top)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button 
            onClick={handleAddGst} 
            color="primary" 
            disabled={!newGst.gstName || !newGst.percentage}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddGst;